import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Loader2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { ProductHeader } from "./components/ProductHeader";
import { ProductTabsNav, type ProductTabDef } from "./components/ProductTabsNav";
import { DirtyFormProvider } from "@/hooks/use-dirty-form";
import {
  listAttachments, fileKeys, type Attachment,
} from "@/lib/files-api";
import type { CustomSpecs } from "@/components/products/CustomSpecsSection";

const InfoTab = lazy(() => import("./tabs/InfoTab"));
const FilesTab = lazy(() => import("./tabs/FilesTab"));
const VendorsTab = lazy(() => import("./tabs/VendorsTab"));
const PricingTab = lazy(() => import("./tabs/PricingTab"));

interface Product {
  id: string;
  sku: string;
  code: string | null;
  name: string;
  description: string | null;
  specs: string | null;
  internalNotes: string | null;
  productClass: string;
  productType: string | null;
  itemType: string;
  categoryId: string | null;
  status: string;
  inventoryUomCode: string | null;
  salesUomCode: string | null;
  purchaseUomCode: string | null;
  basePrice: string | null;
  baseCost: string | null;
  unitPrice: string | null;
  minOrderQty: number | null;
  accountId: string | null;
  isCustomizable: boolean;
  customSpecs: CustomSpecs;
  upc: string | null;
  brand: string | null;
  tempZone?: string | null;
  catchWeight?: boolean | number | null;
  ti?: number | null;
  hi?: number | null;
  shelfLifeDays?: number | null;
  createdAt: string;
  updatedAt: string | null;
  account?: { id: string; name: string };
  category?: { id: string; name: string };
}

interface Category { id: string; name: string }

const TAB_SLUGS = ["info", "files", "vendors", "pricing"] as const;
type TabSlug = (typeof TAB_SLUGS)[number];

function TabSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-64" />
      <Skeleton className="h-32" />
    </div>
  );
}

export default function ProductDetailPage() {
  const { sessionId } = useAuth();
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const activeTab: TabSlug = useMemo(() => {
    if (!tab || !TAB_SLUGS.includes(tab as TabSlug)) return "info";
    return tab as TabSlug;
  }, [tab]);

  // If user landed on an invalid tab slug, normalize the URL to /info.
  useEffect(() => {
    if (tab && !TAB_SLUGS.includes(tab as TabSlug)) {
      setLocation(`/products/${id}/info`, { replace: true });
    }
  }, [tab, id, setLocation]);

  const { data: product, isLoading } = useQuery<Product>({
    queryKey: ["/api/products", id, sessionId],
    queryFn: () => api.get<Product>(`/products/${id}`, sessionId),
    enabled: !!id && !!sessionId,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories", sessionId],
    queryFn: () => api.get<Category[]>(`/categories`, sessionId),
    enabled: !!sessionId,
  });

  // File count for the Files tab badge — sourced from the shared cache.
  const { data: files = [] } = useQuery<Attachment[]>({
    queryKey: [...fileKeys.list("product", id || ""), sessionId],
    queryFn: () => listAttachments("product", id!, sessionId),
    enabled: !!id && !!sessionId,
  });
  const fileCount = files.filter((f) => !f.isDeleted).length;

  const [editForm, setEditForm] = useState<Partial<Product>>({});

  const updateMutation = useMutation({
    mutationFn: () => api.patch(`/products/${id}`, editForm, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      toast({ title: "Product updated" });
      setEditOpen(false);
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/products/${id}`, sessionId),
    onSuccess: () => {
      toast({ title: "Product deleted" });
      setLocation("/products");
    },
    onError: (e: Error) =>
      toast({ title: "Cannot delete product", description: e.message, variant: "destructive" }),
  });

  function openEdit() {
    if (!product) return;
    setEditForm({
      sku: product.sku, name: product.name, description: product.description,
      specs: product.specs, internalNotes: product.internalNotes,
      productClass: product.productClass, itemType: product.itemType,
      categoryId: product.categoryId, status: product.status,
      inventoryUomCode: product.inventoryUomCode, salesUomCode: product.salesUomCode,
      basePrice: product.basePrice, baseCost: product.baseCost, unitPrice: product.unitPrice,
      minOrderQty: product.minOrderQty,
    });
    setEditOpen(true);
  }

  function changeTab(slug: string) {
    setLocation(`/products/${id}/${slug}`, { replace: true });
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }
  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h2 className="text-lg font-medium">Product not found</h2>
        <Button variant="outline" className="mt-4" onClick={() => setLocation("/products")}>
          Back to Products
        </Button>
      </div>
    );
  }

  const tabs: ProductTabDef[] = [
    { slug: "info", label: "Info" },
    { slug: "files", label: "Files", count: fileCount },
    { slug: "vendors", label: "Vendors" },
    { slug: "pricing", label: "Pricing" },
  ];

  const categoryName = product.category?.name
    || categories.find((c) => c.id === product.categoryId)?.name
    || null;

  return (
    <DirtyFormProvider>
      <div className="space-y-4">
        <ProductHeader
          product={{
            id: product.id, sku: product.sku, name: product.name,
            productClass: product.productClass, itemType: product.itemType,
            status: product.status, updatedAt: product.updatedAt,
            createdAt: product.createdAt,
            category: product.category, categoryName: categoryName ?? undefined,
            account: product.account,
          }}
          onEdit={openEdit}
          onDelete={() => setDeleteOpen(true)}
        />

        <ProductTabsNav tabs={tabs} active={activeTab} onChange={changeTab} />

        <Suspense fallback={<TabSkeleton />}>
          {activeTab === "info" && (
            <InfoTab product={product as any} onEditProduct={openEdit} />
          )}
          {activeTab === "files" && (
            <FilesTab entityType="product" entityId={product.id} />
          )}
          {activeTab === "vendors" && (
            <VendorsTab productId={product.id} />
          )}
          {activeTab === "pricing" && (
            <PricingTab
              productId={product.id}
              productClass={product.productClass}
              basePrice={product.basePrice}
              unitPrice={product.unitPrice}
              baseCost={product.baseCost}
            />
          )}
        </Suspense>
      </div>

      {/* Edit drawer */}
      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Product"
        description="Update product information."
        formId="edit-product"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="2xl"
        data-testid="drawer-edit-product"
      >
        <form
          id="edit-product"
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">SKU *</label>
              <Input
                variant="flat"
                className="font-mono"
                value={editForm.sku || ""}
                onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                data-testid="input-sku"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                variant="flat"
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="input-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Product Type</label>
              <Select
                value={editForm.productClass || "stock"}
                onValueChange={(v) => setEditForm({ ...editForm, productClass: v })}
              >
                <SelectTrigger variant="flat" data-testid="select-product-class"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stock">Stock</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Item Type</label>
              <Select
                value={editForm.itemType || "non_inventory"}
                onValueChange={(v) => setEditForm({ ...editForm, itemType: v })}
              >
                <SelectTrigger variant="flat" data-testid="select-item-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory">Inventory</SelectItem>
                  <SelectItem value="non_inventory">Non-Inventory</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="bundle">Bundle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <Select
                value={editForm.status || "active"}
                onValueChange={(v) => setEditForm({ ...editForm, status: v })}
              >
                <SelectTrigger variant="flat" data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="discontinued">Discontinued</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Select
                value={editForm.categoryId || "__none__"}
                onValueChange={(v) =>
                  setEditForm({ ...editForm, categoryId: v === "__none__" ? null : v })
                }
              >
                <SelectTrigger variant="flat" data-testid="select-category"><SelectValue placeholder="No category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Inventory UOM</label>
              <Input
                variant="flat"
                value={editForm.inventoryUomCode || ""}
                onChange={(e) => setEditForm({ ...editForm, inventoryUomCode: e.target.value })}
                placeholder="each"
                data-testid="input-inventory-uom"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Sales UOM</label>
              <Input
                variant="flat"
                value={editForm.salesUomCode || ""}
                onChange={(e) => setEditForm({ ...editForm, salesUomCode: e.target.value })}
                placeholder="each (default)"
                data-testid="input-sales-uom"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Min Order Qty</label>
              <Input
                variant="flat"
                type="number"
                min={1}
                value={editForm.minOrderQty ?? 1}
                onChange={(e) =>
                  setEditForm({ ...editForm, minOrderQty: Number(e.target.value) || 1 })
                }
                data-testid="input-min-order-qty"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Base Price</label>
              <Input
                variant="flat"
                type="number"
                step="0.01"
                value={editForm.basePrice ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, basePrice: e.target.value || null })
                }
                data-testid="input-base-price"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Unit Price</label>
              <Input
                variant="flat"
                type="number"
                step="0.0001"
                value={editForm.unitPrice ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, unitPrice: e.target.value || null })
                }
                data-testid="input-unit-price"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Base Cost</label>
              <Input
                variant="flat"
                type="number"
                step="0.01"
                value={editForm.baseCost ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, baseCost: e.target.value || null })
                }
                data-testid="input-base-cost"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              variant="flat"
              rows={2}
              value={editForm.description ?? ""}
              onChange={(e) =>
                setEditForm({ ...editForm, description: e.target.value || null })
              }
              data-testid="textarea-description"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Specifications</label>
            <Textarea
              variant="flat"
              rows={3}
              value={editForm.specs ?? ""}
              onChange={(e) =>
                setEditForm({ ...editForm, specs: e.target.value || null })
              }
              data-testid="textarea-specs"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Internal Notes</label>
            <Textarea
              variant="flat"
              rows={2}
              value={editForm.internalNotes ?? ""}
              onChange={(e) =>
                setEditForm({ ...editForm, internalNotes: e.target.value || null })
              }
              data-testid="textarea-internal-notes"
            />
          </div>
        </form>
      </FormDrawer>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-medium">{product.name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DirtyFormProvider>
  );
}
